import "./ResultsClusters.css";
import ClusterCard from "../ClusterCard/ClusterCard.js";
import WarningCard from "../WarningCard/WarningCard.js";
import { MESSAGES } from "../../utils/constants.js";
import ConfigManager from "../../utils/ConfigManager.js";

class ResultsClusters {
  constructor() {
    this.analysisResult = null;
    this.warnings = null;
    this.clusterCards = [];
    this.warningCards = [];
  }

  setData({ warnings = null, analysisResult = null }) {
    this.warnings = warnings;
    this.analysisResult = analysisResult;
  }

  setAnalysisResult(analysisResult) {
    this.analysisResult = analysisResult;
  }

  mount(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Container not found: ${selector}`);
    }

    if (!this.analysisResult) {
      container.innerHTML = this.renderNoData();
      return;
    }

    const hasWarnings = this.warnings?.missingWavelengths?.length > 0;
    const hasClusters = this.analysisResult.clustersFound > 0;

    if (!hasWarnings && !hasClusters) {
      container.innerHTML = this.renderNoDuplicates();
      return;
    }

    container.innerHTML = this.renderResults();
    this.mountComponents(container);
  }

  renderNoData() {
    return `
      <div class="results-empty">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
            <line x1="12" y1="11" x2="12" y2="17"></line>
            <line x1="12" y1="11" x2="12" y2="17"></line>
          </svg>
        </div>
        <h3>Aucune donnée à analyser</h3>
        <p>Veuillez importer un fichier Excel pour commencer l'analyse.</p>
      </div>
    `;
  }

  renderNoDuplicates() {
    const { summary } = this.analysisResult;

    return `
      <div class="results-success">
        <div class="success-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22,4 12,14.01 9,11.01"></polyline>
          </svg>
        </div>
        <h3>${MESSAGES.NO_DUPLICATES}</h3>
        <div class="success-stats">
          <div class="stat-item">
            <span class="stat-number">${summary.totalFibers}</span>
            <span class="stat-label">fibres analysées</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">${this.analysisResult.totalComparisons}</span>
            <span class="stat-label">comparaisons effectuées</span>
          </div>
        </div>
        <div class="wavelength-breakdown">
          <h4>Répartition par longueur d'onde</h4>
          ${Object.entries(summary.wavelengthStats)
            .map(
              ([lambda, stats]) => `
            <div class="wavelength-stat">
              <span class="wavelength-label">λ ${lambda}nm:</span>
              <span class="wavelength-count">${stats.total} fibres</span>
            </div>
          `
            )
            .join("")}
        </div>
      </div>
    `;
  }

  renderResults() {
    const hasWarnings = this.warnings?.missingWavelengths?.length > 0;
    const hasClusters = this.analysisResult.clustersFound > 0;

    return `
      <div class="results-container">
        ${hasWarnings ? this.renderWarningsSection() : ""}
        ${hasClusters ? this.renderClustersSection() : ""}
      </div>
    `;
  }

  renderWarningsSection() {
    const warningCount = this.warnings.missingWavelengths.length;

    return `
      <div class="warnings-section">
        <div class="warnings-header">
          <h2 class="warnings-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            ${warningCount} Avertissement${warningCount > 1 ? "s" : ""} détecté${warningCount > 1 ? "s" : ""}
          </h2>
          <p class="warnings-description">
            Les fibres suivantes ont des longueurs d'onde manquantes et doivent être vérifiées :
          </p>
        </div>
        <div class="warnings-grid" id="warnings-container">
          <!-- WarningCards seront montées ici -->
        </div>
      </div>
    `;
  }

  renderClustersSection() {
    const { clustersFound, summary } = this.analysisResult;
    const config = ConfigManager.getConfig();

    return `
      <div class="clusters-section">
        <div class="clusters-header">
          <h2 class="clusters-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
            ${clustersFound} ${MESSAGES.DUPLICATES_FOUND}
          </h2>
          <div class="clusters-summary">
            <div class="summary-stats">
              <div class="stat-card">
                <div class="stat-number">${summary.totalFibers}</div>
                <div class="stat-label">Fibres analysées</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">${summary.fibersInClusters}</div>
                <div class="stat-label">Fibres en doublon</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">${this.analysisResult.totalComparisons}</div>
                <div class="stat-label">Comparaisons</div>
              </div>
            </div>
            <div class="config-display">
              <span class="config-label">Seuils actifs:</span>
              <span class="config-value">Moy Conn: ${config.seuilMoyenneConnecteurs} dB</span>
              <span class="config-separator">|</span>
              <span class="config-value">Moy Epis: ${config.seuilMoyenneEpissures} dB</span>
              <span class="config-separator">|</span>
              <span class="config-value">Perte Tot: ${config.seuilPerteTotale} dB</span>
            </div>
          </div>
        </div>
        
        <div class="clusters-grid" id="clusters-container">
          <!-- ClusterCards seront montées ici -->
        </div>
      </div>
    `;
  }

  mountComponents(container) {
    this.mountWarningCards(container);
    this.mountClusterCards(container);
  }

  mountWarningCards(container) {
    const warningsContainer = container.querySelector("#warnings-container");
    if (!warningsContainer || !this.warnings?.missingWavelengths?.length) return;

    this.warningCards = [];

    this.warnings.missingWavelengths.forEach((warning) => {
      const warningCard = new WarningCard(warning);
      this.warningCards.push(warningCard);

      const cardContainer = document.createElement("div");
      warningsContainer.appendChild(cardContainer);

      warningCard.mount(cardContainer);
    });
  }

  mountClusterCards(container) {
    const clustersContainer = container.querySelector("#clusters-container");
    if (!clustersContainer || !this.analysisResult?.clusters?.length) return;

    this.clusterCards = [];

    this.analysisResult.clusters.forEach((cluster) => {
      const clusterCard = new ClusterCard(cluster);
      this.clusterCards.push(clusterCard);

      const cardContainer = document.createElement("div");
      clustersContainer.appendChild(cardContainer);
      clusterCard.mount(cardContainer);
    });
  }

  clear() {
    this.analysisResult = null;
    this.warnings = null;
    this.clusterCards = [];
    this.warningCards = [];
  }
}

export default ResultsClusters;
